import { useEffect } from "react";
import { CountdownBannerCtaProps } from "../types";
import CTALink from "@/components/CTA/ctaLink";
import { useInView } from "react-intersection-observer";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import { Course } from "@/components/ProductCardListWrapper/variants/ProductCardListByQuery/docs/index.stories";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import PromoBannerTooltip from "@/components/PromoBanner/components/PromoBannerTooltip";
import useTranslations from "@/i18n/useTranslations";

export default function CountdownBannerCta({
  theme,
  header,
  subHeader,
  showSubheader,
  countdownTime,
  htmlId,
  location,
  name,
  position,
  showCTA,
  ctaElement,
  tooltip,
}: CountdownBannerCtaProps) {
  const t = useTranslations("Common");
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Banner,
      htmlId: htmlId,
      location: location,
      name: name,
      position: position,
    },
  });

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: htmlId,
      location: location,
      name: name,
      position: position,
    },
  });

  const { ref: bannerRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);
  if (!countdownTime) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="flex flex-col w-full items-center lg:items-start bg-primary-500 text-white p-8 rounded-[8px] min-h-[150px]"
    >
      <div className="flex flex-col gap-y-4 mb-4 lg:mb-10 flex-1">
        <span className="font-bold elm-h3">
          {sanitizeAndParseInlineHTML(header)}
        </span>
        {showSubheader && subHeader && (
          <span className="hidden xl:block font-normal elm-p3 gap-1 items-center">
            {sanitizeAndParseInlineHTML(subHeader)}
            {tooltip ? (
              <PromoBannerTooltip htmlContent={tooltip?.infoText} size={16} />
            ) : null}
          </span>
        )}
      </div>
      <div className="flex flex-col lg:flex-row lg:gap-x-10 min-h-20 justify-between items-center w-full ">
        <div
          className={`flex flex-row w-full lg:w-auto items-center justify-center mb-6 lg:mb-4 p-4 h-full ${theme === "light" ? "text-primary-500 bg-putty-100 rounded-xl p-4" : ""}`}
        >
          <span className="font-bold elm-h3 mr-1">{countdownTime.days}</span>
          <span className="elm-p3 xl:elm-p5">{t("days")}</span>
          <span className="mx-2 lg:mx-2 elm-h3 font-bold">:</span>
          <span className="font-bold elm-h3 mr-1">{countdownTime.hours}</span>
          <span className="elm-p3 xl:elm-p5">{t("hours")}</span>
          <span className="mx-2 lg:mx-2 elm-h3 font-bold">:</span>
          <span className="font-bold elm-h3 mr-1">{countdownTime.minutes}</span>
          <span className="elm-p3 xl:elm-p5">{t("minutes")}</span>
        </div>
        {showSubheader && subHeader && (
          <span className="xl:hidden font-normal mb-4 elm-p3">
            {sanitizeAndParseInlineHTML(subHeader)}
            {tooltip ? (
              <PromoBannerTooltip htmlContent={tooltip?.infoText} size={16} />
            ) : null}
          </span>
        )}
        {showCTA && <div className="">{ctaElement}</div>}
      </div>
    </div>
  );
}
